import VideoPlug from '../../common/VideoPlug'
import AudioPlug from '../../common/AudioPlug'
import PeerConnection from './PeerConnection'
import Logger from '../../common/Logger'

const CAM_WIDTH = 640
const CAM_HEIGHT = 480
const CAM_FRAMES = 15

const DEFAULT_VIDEO_STREAM_OPTIONS = Object.freeze({
    width: {ideal: CAM_WIDTH},
    height: {ideal: CAM_HEIGHT},
    frameRate: {ideal: CAM_FRAMES},
})

const SHARING_WIDTH = 1920
const SHARING_HEIGHT = 1080
const SHARING_FRAMES = 10

const DEFAULT_SHARING_OPTIONS = Object.freeze({
    width: SHARING_WIDTH,
    height: SHARING_HEIGHT,
    frameRate: {ideal: SHARING_FRAMES},
})

const DIRECTION = {
    SEND_ONLY: 'sendonly',
    RECV_ONLY: 'recvonly',
    SEND_RECV: 'sendrecv',
}

const LOG_STRING_LIMIT = 2000
const LOG_ARRAY_LIMIT = 20

const describeTrack = (track) => {
    if (!track) return null
    return {
        id: track.id,
        kind: track.kind,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
        label: track.label,
    }
}

const describeStream = (stream) => {
    if (!stream) return null
    const tracks = (stream.getTracks && stream.getTracks()) || []
    return {
        id: stream.id,
        active: stream.active,
        tracks: tracks.map((track) => describeTrack(track)),
    }
}

const describeMediaElement = (el) => {
    if (!el) return null
    return {
        tag: el.tagName,
        id: el.id,
        muted: el.muted,
        volume: el.volume,
        readyState: el.readyState,
        paused: el.paused,
    }
}

const safeStringify = (value) => {
    if (value === undefined) return 'undefined'
    if (value === null) return 'null'
    if (typeof value === 'string') return value
    if (typeof value !== 'object') return String(value)
    const seen = new WeakSet()
    const replacer = (key, val) => {
        if (key === 'sdp' && typeof val === 'string') return `<sdp:${val.length}>`
        if (typeof val === 'bigint') return val.toString()
        if (typeof val === 'function') return `[Function${val.name ? ':' + val.name : ''}]`
        if (Array.isArray(val) && val.length > LOG_ARRAY_LIMIT) {
            return {length: val.length, preview: val.slice(0, LOG_ARRAY_LIMIT)}
        }
        if (val instanceof Error) {
            return {message: val.message, stack: val.stack}
        }
        if (typeof MediaStream !== 'undefined' && val instanceof MediaStream) {
            return describeStream(val)
        }
        if (typeof MediaStreamTrack !== 'undefined' && val instanceof MediaStreamTrack) {
            return describeTrack(val)
        }
        if (typeof RTCPeerConnection !== 'undefined' && val instanceof RTCPeerConnection) {
            return {
                connectionState: val.connectionState,
                iceConnectionState: val.iceConnectionState,
                signalingState: val.signalingState,
            }
        }
        if (typeof RTCSessionDescription !== 'undefined' && val instanceof RTCSessionDescription) {
            return {type: val.type, sdpLength: val.sdp ? val.sdp.length : 0}
        }
        if (typeof RTCIceCandidate !== 'undefined' && val instanceof RTCIceCandidate) {
            return {candidate: val.candidate, sdpMid: val.sdpMid, sdpMLineIndex: val.sdpMLineIndex}
        }
        if (typeof MediaDeviceInfo !== 'undefined' && val instanceof MediaDeviceInfo) {
            return {kind: val.kind, deviceId: val.deviceId, label: val.label, groupId: val.groupId}
        }
        if (typeof HTMLMediaElement !== 'undefined' && val instanceof HTMLMediaElement) {
            return describeMediaElement(val)
        }
        if (val instanceof Map) return Array.from(val.entries())
        if (val instanceof Set) return Array.from(val.values())
        if (val && typeof val === 'object') {
            if (seen.has(val)) return '[Circular]'
            seen.add(val)
        }
        return val
    }
    try {
        const str = JSON.stringify(value, replacer)
        if (str && str.length > LOG_STRING_LIMIT) {
            return `${str.slice(0, LOG_STRING_LIMIT)}...<truncated>`
        }
        return str
    } catch (e) {
        return '[Unserializable]'
    }
}

const buildLogMessage = (message, data) => {
    if (data === undefined) return message
    return `${message} | ${safeStringify(data)}`
}

class WebRTCClientNew {
    constructor ({
                     videoPlugText = 'Roschat WebRTCClient',
                     audioDummy = true,
                     volume = 100,
                     localEl = null,
                     sharingEl = null,
                     audioMixingEl = null,
                     video = false,
                     iceServers = [],
                     logger,
                     debug = true
                 } = {}) {
        this.video = video
        this.localEl = localEl
        this.sharingEl = sharingEl
        this.audioMixingEl = audioMixingEl
        this.remoteId = 0
        this.remoteVideoEls = new Map()
        this.remoteAudioEls = new Map()
        this.remoteStreams = new Map()
        this.videoPlugText = videoPlugText
        this.muted = false
        this.mutedVideo = false
        this.mutedSpeaker = false
        this.isHold = false
        this.audioDummy = audioDummy
        this.videoPlug = null
        this.audioPlug = null
        this.localStream = null
        this.localSharingStream = null
        this.sharingStream = null
        this.audioMixingStream = null
        this.volume = volume
        this.peers = new Map()
        // centralized track storage for potential aggregated SDP handling
        this.localTracks = new Map()   // trackId -> MediaStream|null
        this.remoteTracks = new Map()  // trackId -> { peerId, stream }
        this.iceServers = iceServers
        this.debug = debug
        this.logger = logger || new Logger('WebRTCClient')
        this._logDebug('constructor', {
            video,
            audioDummy,
            volume,
            localEl: describeMediaElement(localEl),
            sharingEl: describeMediaElement(sharingEl),
            audioMixingEl: describeMediaElement(audioMixingEl),
            iceServers,
        })
    }

    _logInfo(message, data) {
        const text = buildLogMessage(message, data)
        if (this.logger.info) this.logger.info(text)
        else console.log(text)
    }

    _logWarn(message, data) {
        const text = buildLogMessage(message, data)
        if (this.logger.warn) this.logger.warn(text)
        else this.logger.info(text)
    }

    _logDebug(message, data) {
        if (!this.debug) return
        const text = buildLogMessage(message, data)
        if (this.logger.info) this.logger.info(text)
        else console.log(text)
    }

    _logError(error, description, data) {
        const desc = description ? buildLogMessage(description, data) : (data !== undefined ? buildLogMessage('error', data) : undefined)
        if (this.logger.error) this.logger.error(error, desc)
        else this.logger.info(buildLogMessage('error', {error: String(error), description: desc}))
    }

    setIceServers (iceServers = []) {
        this.iceServers = iceServers
        this._logDebug('setIceServers', {count: iceServers.length})
    }

    setLocalEl(el) {
        this.localEl = el
        if (this.localStream) this.setLocalStream(this.localStream)
        if (this.localEl) {
            this.localTracks.forEach((stream) => {
                if (stream) this._attachStream(this.localEl, stream)
            })
        }
        this._logDebug('setLocalEl', describeMediaElement(el))
    }

    setSharingEl(el) {
        this.sharingEl = el
        if (this.sharingStream) this.setSharingStream(this.sharingStream)
        this._logDebug('setSharingEl', describeMediaElement(el))
    }

    setAudioMixingEl(el) {
        this.audioMixingEl = el
        if (this.audioMixingStream) this.setAudioMixingStream(this.audioMixingStream)
        this._logDebug('setAudioMixingEl', describeMediaElement(el))
    }

    addRemoteEl(videoEl, audioEl, id) {
        if (!id) id = this.remoteId++
        if (videoEl) this.remoteVideoEls.set(id, videoEl)
        if (audioEl) this.remoteAudioEls.set(id, audioEl)
        const stream = this.remoteStreams.get(id)
        if (stream) this._attachRemoteStream(id, stream)
        this._logDebug('addRemoteEl', {
            id,
            videoEl: describeMediaElement(videoEl),
            audioEl: describeMediaElement(audioEl),
        })
        return id
    }

    removeRemoteEl(id) {
        if (id) {
            const videoEl = this.remoteVideoEls.get(id)
            const audioEl = this.remoteAudioEls.get(id)
            if (videoEl) this._attachStream(videoEl, null)
            if (audioEl) this._attachStream(audioEl, null)
            this.remoteVideoEls.delete(id)
            this.remoteAudioEls.delete(id)
            this.remoteStreams.delete(id)
            this._logDebug('removeRemoteEl', {id})
        }
    }

    getLocalStream() {
        return this.localStream
    }

    getRemoteStream(id = 0) {
        return this.remoteStreams.get(id)
    }

    getRemoteVideoEl(id = 0) {
        return this.remoteVideoEls.get(id)
    }

    setLocalStream(stream) {
        const localVideo = this.localEl
        if (stream) {
            this._attachStream(localVideo, stream)
            this.localStream = stream
            this._logDebug('setLocalStream', describeStream(stream))
        } else {
            if (localVideo) localVideo.srcObject = null
            if (this.localStream) this.localStream.stop && this.localStream.stop()
            this._logDebug('setLocalStream', null)
        }
    }

    setRemoteStream(stream, id) {
        const remoteVideo = this.getRemoteVideoEl(id)
        const remoteAudio = this.remoteAudioEls.get(id)
        if (!remoteVideo && !remoteAudio) {
            this._logWarn('setRemoteStream: remote elements missing', {id})
            return
        }
        this._attachRemoteStream(id, stream)
        if (stream) this.remoteStreams.set(id, stream)
        else this.remoteStreams.delete(id)
        this._logDebug('setRemoteStream', {id, stream: describeStream(stream)})
    }

    _attachRemoteStream(id, stream) {
        const videoEl = this.remoteVideoEls.get(id)
        const audioEl = this.remoteAudioEls.get(id)
        const hasStream = Boolean(stream && stream.getTracks)
        const videoTracks = hasStream && stream.getVideoTracks ? stream.getVideoTracks() : []
        const audioTracks = hasStream && stream.getAudioTracks ? stream.getAudioTracks() : []
        const videoStream = videoTracks.length ? new MediaStream(videoTracks) : null
        const audioStream = audioTracks.length ? new MediaStream(audioTracks) : null
        if (videoEl) this._attachStream(videoEl, videoStream)
        if (audioEl) this._attachStream(audioEl, audioStream)
    }

    _attachStream(el, stream) {
        if (!el) return
        if (stream) {
            if ('srcObject' in el) el.srcObject = stream
            else el.src = window.URL.createObjectURL(stream)
            el.onloadedmetadata = () => el.play && el.play()
        } else {
            el.srcObject = null
        }
    }

    async startLocal(el, options) {
        if (el) this.setLocalEl(el)
        if (!this.localEl) this._logWarn('startLocal: local element is missing')
        return new Promise(async (resolve, reject) => {
            let stream
            try {
                this._logDebug('startLocal: getAudioStream', {options: {audio: options.audio}})
                stream = await this.getAudioStream({...{audio: options.audio}})
                if (options.video || this.video) {
                    this._logDebug('startLocal: getVideoStream', {options: {video: options.video}})
                    let videoStream = await this.getVideoStream({...{video: options.video}})
                    stream = new MediaStream([...stream.getTracks(), ...videoStream.getTracks()])
                }
            } catch (e) {
                this._logError(e, 'startLocal: getUserMedia() error', {options})
                reject(e)
            }
            if(stream && !stream.stop && stream.getTracks) {
                stream.stop = () => stream.getTracks().forEach((track) => track.stop())
            }
            this.setLocalStream(stream)
            const audioTracks = stream?.getAudioTracks ? stream.getAudioTracks() : []
            const videoTracks = stream?.getVideoTracks ? stream.getVideoTracks() : []
            if (options.audio && audioTracks.length === 0) {
                this._logWarn('startLocal: audio requested but no tracks', describeStream(stream))
            }
            if (options.video && videoTracks.length === 0) {
                this._logWarn('startLocal: video requested but no tracks', describeStream(stream))
            }
            resolve([])
        })
    }

    async getAudioStream(options) {
        let audioStream
        try {
            audioStream = await navigator.mediaDevices.getUserMedia(options)
            this._logDebug('getAudioStream: success', describeStream(audioStream))
        } catch (e) {
            if (this.audioDummy) {
                this.audioPlug = new AudioPlug()
                audioStream = this.audioPlug.start()
                this._logWarn('getAudioStream: fallback to AudioPlug', describeStream(audioStream))
            } else {
                throw e
            }
        }
        return audioStream
    }

    async getVideoStream(options) {
        let videoStream
        try {
            if (options.video) options.video = { ...DEFAULT_VIDEO_STREAM_OPTIONS, ...options.video }
            videoStream = await navigator.mediaDevices.getUserMedia(options)
            this._logDebug('getVideoStream: success', describeStream(videoStream))
        } catch (e) {
            this.videoPlug = new VideoPlug({text: this.videoPlugText, width: CAM_WIDTH, height: CAM_HEIGHT, frames: CAM_FRAMES })
            videoStream = this.videoPlug.start()
            this._logWarn('getVideoStream: fallback to VideoPlug', describeStream(videoStream))
        }
        return videoStream
    }

    async startLocalSharing(options) {
        try {
            let stream
            if (navigator.getDisplayMedia) {
                stream = await navigator.getDisplayMedia({video: {...DEFAULT_SHARING_OPTIONS, ...options?.video}})
            } else {
                stream = await navigator.mediaDevices.getDisplayMedia({video: {...DEFAULT_SHARING_OPTIONS, ...options?.video}})
            }
            this.setLocalSharingStream(stream)
            this._logDebug('startLocalSharing', describeStream(stream))
            return stream
        } catch (e) {
            this._logError(e, 'startLocalSharing error', {options})
            throw e
        }
    }

    setLocalSharingStream(stream) {
        if (stream) {
            this.localSharingStream = stream
            if(stream && !stream.stop && stream.getTracks) {
                stream.stop = () => stream.getTracks().forEach((track) => track.stop())
            }
            this._logDebug('setLocalSharingStream', describeStream(stream))
        } else {
            if (this.localSharingStream) this.localSharingStream.stop()
            this._logDebug('setLocalSharingStream', null)
        }
    }

    setSharingStream(stream) {
        const sharingEl = this.sharingEl
        if (!sharingEl) {
            this._logWarn('setSharingStream: sharing element missing')
            return
        }
        if (stream) {
            this._attachStream(sharingEl, stream)
            this.sharingStream = stream
            this._logDebug('setSharingStream', describeStream(stream))
        } else {
            this._attachStream(sharingEl, null)
            this._logDebug('setSharingStream', null)
        }
    }

    setAudioMixingStream(stream) {
        const audioMixingEl = this.audioMixingEl
        if (!audioMixingEl) {
            this._logWarn('setAudioMixingStream: audio mixing element missing')
            return
        }
        if (stream) {
            this._attachStream(audioMixingEl, stream)
            this.audioMixingStream = stream
            this._logDebug('setAudioMixingStream', describeStream(stream))
        } else {
            this._attachStream(audioMixingEl, null)
            this._logDebug('setAudioMixingStream', null)
        }
    }

    updateStreams() {
        setTimeout(()=> {
            this.setLocalStream(this.localStream)
            this.remoteStreams.forEach((stream, id) => this.setRemoteStream(stream, id))
        }, 0);
    }

    stop() {
        this._logDebug('stop')
        this.peers.forEach(({pc}) => pc && pc.close())
        this.peers.clear()
        this.localTracks.clear()
        this.remoteTracks.clear()
        this.setLocalStream(null)
        this.setLocalSharingStream(null)
        this.setAudioMixingStream(null)
        this.remoteStreams.forEach((stream, id) => this.setRemoteStream(null, id))
        this.remoteStreams.clear()
        if (this.videoPlug) this.videoPlug.stop()
        if (this.audioPlug) this.audioPlug.stop()
    }

    mute() {
        if (!this.localStream) return
        let enabled = true
        if (this.localStream && this.localStream.getAudioTracks) {
            enabled = !this.localStream.getAudioTracks()[0].enabled
            this.localStream.getAudioTracks()[0].enabled = enabled
        } else {
            this._logWarn('mute: localStream getAudioTracks error')
        }
        this.muted = !enabled
        this._logDebug('mute', {muted: this.muted})
        return this.muted
    }

    muteVideo() {
        if (!this.localStream) return
        let enabled = true
        if (this.localStream && this.localStream.getVideoTracks) {
            enabled = !this.localStream.getVideoTracks()[0].enabled
            this.localStream.getVideoTracks()[0].enabled = enabled
        } else {
            this._logWarn('muteVideo: localStream getVideoTracks error')
        }
        this.mutedVideo = !enabled
        this._logDebug('muteVideo', {muted: this.mutedVideo})
        return this.mutedVideo
    }

    muteSpeaker() {
        this.mutedSpeaker = !this.mutedSpeaker
        this.remoteStreams.forEach((stream) => this._streamSpeakerEnabledApply(stream))
        this._logDebug('muteSpeaker', {muted: this.mutedSpeaker})
        return this.mutedSpeaker
    }

    _streamSpeakerEnabledApply(stream) {
        const enabled = !this.mutedSpeaker
        stream.getAudioTracks().forEach((track) => {
            if (track.enabled !== enabled) track.enabled = enabled
        })
    }

    hold(value, isVideo) {
        if (!this.localStream) return
        if (!this.muted) this.localStream.getAudioTracks()[0].enabled = !value
        if (isVideo) this.localStream.getVideoTracks()[0].enabled = !value
        this.remoteStreams.forEach((stream) => {
            stream.getAudioTracks().forEach((t) => t.enabled = !value)
            if(isVideo) stream.getVideoTracks().forEach((t) => t.enabled = !value)
        })
        this.isHold = value
        this._logDebug('hold', {value, isVideo})
    }

    getIsHold() {
        return this.isHold
    }

    changeSpeakerVolume(data) {
        this.volume = data
        this.remoteVideoEls.forEach((el) => el.volume = data / 100)
        this.remoteAudioEls.forEach((el) => el.volume = data / 100)
        this._logDebug('changeSpeakerVolume', {volume: data})
    }

    async replaceLocalAudioDevice(id) {
        if (!this.localStream) return
        this._logDebug('replaceLocalAudioDevice: start', {deviceId: id})
        const stream = await navigator.mediaDevices.getUserMedia({audio: {deviceId: { exact: id }}})
        const newTrack = stream.getAudioTracks()[0]
        const oldTrack = this.localStream.getAudioTracks()[0]
        this.localStream.addTrack(newTrack)
        await this._replaceTrackOnPeers('audio', oldTrack, newTrack)
        this._removeLocalTrack(oldTrack)
        this._logDebug('replaceLocalAudioDevice: done', {oldTrack: describeTrack(oldTrack), newTrack: describeTrack(newTrack)})
        return { oldTrack, newTrack }
    }

    async replaceLocalVideoDevice(id) {
        if (!this.localStream) return
        this._logDebug('replaceLocalVideoDevice: start', {deviceId: id})
        const stream = await this.getVideoStream({video: {deviceId: { exact: id }}})
        const newTrack = stream.getVideoTracks()[0]
        const oldTrack = this.localStream.getVideoTracks()[0]
        this.localStream.addTrack(newTrack)
        await this._replaceTrackOnPeers('video', oldTrack, newTrack)
        this._removeLocalTrack(oldTrack)
        this._logDebug('replaceLocalVideoDevice: done', {oldTrack: describeTrack(oldTrack), newTrack: describeTrack(newTrack)})
        return { oldTrack, newTrack }
    }

    _removeLocalTrack(track) {
        if (this.videoPlug && this.videoPlug.stream.getTracks().find(t => t === track)) {
            this.videoPlug.stop()
            this.videoPlug = null
        }
        if (this.audioPlug && this.audioPlug.stream.getTracks().find(t => t === track)) {
            this.audioPlug.stop()
            this.audioPlug = null
        }
        track.stop && track.stop()
        if (this.localStream) this.localStream.removeTrack(track)
    }

    async _replaceTrackOnPeers(kind, oldTrack, newTrack) {
        const replaceTasks = []
        this.peers.forEach(({pc, direction}) => {
            if (!pc || direction === DIRECTION.RECV_ONLY) return
            const sender = pc.pc.getSenders().find((s) => s?.track?.kind === kind)
            if (sender) {
                replaceTasks.push(sender.replaceTrack(newTrack))
            }
        })
        await Promise.all(replaceTasks)
        this._logDebug('_replaceTrackOnPeers', {kind, count: replaceTasks.length})
    }

    replaceRemoteAudioDevice(id) {
        this.remoteAudioEls.forEach((el) => {
            if (el.setSinkId) el.setSinkId(id)
        })
        this.remoteVideoEls.forEach((el) => {
            if (el.setSinkId) el.setSinkId(id)
        })
        this._logDebug('replaceRemoteAudioDevice', {deviceId: id})
    }

    async createOffer ({id, iceServers, localStream, direction, onRemoteStream, onIceCandidate}) {
        this._logDebug('createOffer: start', {id, direction, localStream: describeStream(localStream)})
        const peer = this._createPeerConnection(id, {iceServers, localStream, direction, onRemoteStream, onIceCandidate})
        const offerPromise = new Promise((resolve) => {
            const handler = (jsep) => {
                peer.pc.off && peer.pc.off('offer', handler)
                resolve(jsep)
            }
            peer.pc.on('offer', handler)
        })
        await peer.pc.createOffer({tracks: []})
        const offer = await offerPromise
        this._logDebug('createOffer: done', {id, offer})
        return offer
    }

    async createAnswer ({id, iceServers, localStream, remoteOffer, direction, onRemoteStream, onIceCandidate}) {
        this._logDebug('createAnswer: start', {id, direction, remoteOffer})
        const peer = this._createPeerConnection(id, {iceServers, localStream, direction, onRemoteStream, onIceCandidate})
        const answerPromise = new Promise((resolve) => {
            const handler = (jsep) => {
                peer.pc.off && peer.pc.off('answer', handler)
                resolve(jsep)
            }
            peer.pc.on('answer', handler)
        })
        await peer.pc.createAnswer({jsep: remoteOffer, tracks: []})
        const answer = await answerPromise
        this._logDebug('createAnswer: done', {id, answer})
        return answer
    }

    async addIceCandidate(id, candidate) {
        const peer = this.peers.get(id)
        if (peer && peer.pc) {
            peer.pc.addCandidate(candidate)
            this._logDebug('addIceCandidate', {id, candidate})
        }
    }

    async setRemoteAnswer(id, answer) {
        const peer = this.peers.get(id)
        if (peer && peer.pc) {
            await peer.pc.setAnswer(answer)
            this._logDebug('setRemoteAnswer', {id, answer})
        }
    }

    stopPeer(id) {
        const peer = this.peers.get(id)
        if (peer && peer.pc) peer.pc.close()
        this.peers.delete(id)
        this.removeRemoteEl(id)
        Array.from(this.remoteTracks.entries()).forEach(([trackId, meta]) => {
            if (meta.peerId === id) this.remoteTracks.delete(trackId)
        })
        this._logDebug('stopPeer', {id})
    }

    _createPeerConnection(id, {iceServers, localStream, direction, onRemoteStream, onIceCandidate}) {
        if (this.peers.has(id)) {
            const oldPeer = this.peers.get(id)
            oldPeer.pc && oldPeer.pc.close()
        }
        this._logDebug('_createPeerConnection', {
            id,
            direction,
            localStream: describeStream(localStream),
            iceServers: iceServers || this.iceServers,
        })
        const pc = new PeerConnection({
            config: { iceServers: iceServers || this.iceServers, trickle: false },
            debug: this.debug,
            logger: this.logger
        })
        pc.init()
        pc.setMediaDevices(navigator.mediaDevices)
        pc.streamExternal = true
        pc.myStream = localStream || null
        this._prepareTransceivers(pc, localStream, direction)

        // Diagnostic events similar to original MediaAdapter usage
        pc.on('logging', (str) => this._logDebug(`pc(${id})`, str))
        pc.on('connection-state', (state) => {
            this._logDebug(`pc(${id}) connection-state`, {state})
            if (state === 'failed' || state === 'disconnected') {
                this._logWarn(`pc(${id}) connection-state warning`, {state})
            }
        })
        pc.on('ice-connection-state', (state) => {
            this._logDebug(`pc(${id}) ice-connection-state`, {state})
            if (state === 'failed' || state === 'disconnected') {
                this._logWarn(`pc(${id}) ice-connection-state warning`, {state})
            }
        })
        pc.on('candidate', (candidate) => {
            this._logDebug(`pc(${id}) candidate`, candidate)
            if (candidate && onIceCandidate) onIceCandidate(candidate)
        })
        pc.on('local-track', ({track, added}) => {
            this._logDebug(`pc(${id}) local-track`, {added, track: describeTrack(track)})
            this._storeLocalTrack(track, added)
        })
        pc.on('remote-track', ({track, added, metadata}) => {
            this._logDebug(`pc(${id}) remote-track`, {added, metadata, track: describeTrack(track)})
            this._handleRemoteTrack(id, track, added, onRemoteStream, metadata)
        })

        const peerEntry = {
            pc,
            direction: direction || DIRECTION.SEND_RECV,
            onRemoteStream
        }
        this.peers.set(id, peerEntry)
        return peerEntry
    }

    _prepareTransceivers (pc, localStream, direction) {
        const dir = direction || DIRECTION.SEND_RECV
        const canSend = dir === DIRECTION.SEND_ONLY || dir === DIRECTION.SEND_RECV
        const canRecv = dir === DIRECTION.RECV_ONLY || dir === DIRECTION.SEND_RECV

        this._logDebug('_prepareTransceivers', {
            direction: dir,
            canSend,
            canRecv,
            localStream: describeStream(localStream),
        })

        if (canSend && localStream) {
            localStream.getTracks().forEach((track) => {
                const sender = pc.pc.addTrack(track, localStream)
                const transceiver = pc.pc.getTransceivers().find((t) => t.sender === sender)
                if (transceiver && dir === DIRECTION.SEND_ONLY) {
                    if (transceiver.setDirection) transceiver.setDirection(DIRECTION.SEND_ONLY)
                    else transceiver.direction = DIRECTION.SEND_ONLY
                }
            })
        }

        if (canRecv && (!localStream || dir === DIRECTION.RECV_ONLY)) {
            // Prepare recvonly transceivers to accept incoming audio/video
            pc.pc.addTransceiver('audio', { direction: DIRECTION.RECV_ONLY })
            pc.pc.addTransceiver('video', { direction: DIRECTION.RECV_ONLY })
        }
    }

    _handleRemoteTrack(id, track, added, onRemoteStream) {
        this._storeRemoteTrack(id, track, added)
        let stream = this.remoteStreams.get(id)
        if (!stream) {
            if (!added) return
            stream = new MediaStream()
        }
        const tracks = stream.getTracks ? stream.getTracks() : []
        const hasTrack = !!track && tracks.some((t) => t.id === track.id)
        let changed = false
        if (added) {
            if (!hasTrack) {
                stream.addTrack(track)
                changed = true
            }
        } else if (hasTrack) {
            stream.removeTrack(track)
            changed = true
        }
        if (changed) {
            this.remoteStreams.set(id, stream)
            this._attachRemoteStream(id, stream)
            if (onRemoteStream) onRemoteStream(stream)
        }
    }

    _trackId (track) {
        return track && track.id ? track.id.replace(/[{}]/g, '') : ''
    }

    _storeLocalTrack(track, added) {
        const trackId = this._trackId(track)
        if (!trackId) return
        if (!added) {
            this.localTracks.delete(trackId)
            return
        }
        // избегаем локального аудио-эхо, но фиксируем наличие трека
        if (track.kind === 'audio') {
            this.localTracks.set(trackId, null)
            return
        }
        const stream = new MediaStream([track])
        this.localTracks.set(trackId, stream)
        if (this.localEl) this._attachStream(this.localEl, stream)
        this._logDebug('_storeLocalTrack', {track: describeTrack(track)})
    }

    _storeRemoteTrack(peerId, track, added) {
        const trackId = this._trackId(track)
        if (!trackId) return
        if (!added) {
            this.remoteTracks.delete(trackId)
            return
        }
        const stream = new MediaStream([track])
        this.remoteTracks.set(trackId, { peerId, stream })
        this._logDebug('_storeRemoteTrack', {peerId, track: describeTrack(track)})
    }
}

export default WebRTCClientNew
